/**
 * AP-49 8. — **Offene Punkte aus Besprechungen** im Listen-Standard: FilterBar, Spalten über
 * `ui-settings`, Sortierung, Seiten, Export samt Mail. Je Serie die offenen Aufgaben des letzten
 * freigegebenen Protokolls; überfällige Termine hervorgehoben. Ein Klick öffnet das Protokoll.
 *
 * Gefiltert, sortiert und geblättert auf dem Server. Muster: `SiteDocsListPage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Settings } from 'lucide-react'
import type { BesprechungOffenerPunkt } from '@symphenia/shared-types'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { ExportButton } from '@/components/ui/ExportButton'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology } from '@/lib/useTerminology'
import { offenePunkte, offenePunkteExport, type OffenePunkteParams } from '../arten/besprechung/api'

const LAYOUT_KEY = 'columnbar_layout_site_docs_meeting_open_items'

interface ColDef {
  key: string
  label: string
  sortable?: boolean
}

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}

const DEFAULT_COLUMN_KEYS = ['projectLabel', 'art', 'text', 'assigneeName', 'dueDate', 'since', 'lastDiscussed']
const DEFAULT_FILTER_FIELD_KEYS = ['q', 'art', 'overdue', 'internal']
const STANDARD: ColumnState = { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortKey: 'dueDate', sortDir: 'asc' }
const SORTIERBAR = new Set(['projectLabel', 'art', 'number', 'assigneeName', 'dueDate', 'since', 'lastDiscussed'])

function filterParams(filters: Record<string, ActiveFilter>): OffenePunkteParams {
  const p: OffenePunkteParams = {}
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'q') p.q = a.value
    if (key === 'art') p.art = a.value
    if (key === 'overdue' && a.value === 'true') p.overdue = true
    if (key === 'internal') p.internal = a.value === 'true'
    if (key === 'projectId') p.projectId = a.value
  }
  return p
}

export default function BesprechungOffenePunktePage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const navigate = useNavigate()

  const filterFields: FilterFieldDef[] = useMemo(
    () => [
      { key: 'q', label: t('siteDocs.filter.q'), type: 'text', category: 'core', operators: ['contains'] },
      { key: 'art', label: t('siteDocs.meeting.type'), type: 'text', category: 'core', operators: ['is'] },
      { key: 'overdue', label: t('siteDocs.meeting.openItems.onlyOverdue'), type: 'boolean', category: 'core', operators: ['is'] },
      {
        key: 'internal',
        label: t('siteDocs.meeting.openItems.internal'),
        type: 'select',
        category: 'core',
        operators: ['is'],
        options: [
          { value: 'true', label: t('siteDocs.meeting.openItems.internalYes') },
          { value: 'false', label: t('siteDocs.meeting.openItems.internalNo') },
        ],
      },
    ],
    [t],
  )
  const alleSpalten: ColDef[] = useMemo(
    () => [
      { key: 'projectLabel', label: terms.project, sortable: true },
      { key: 'art', label: t('siteDocs.meeting.openItems.columns.art'), sortable: true },
      { key: 'text', label: t('siteDocs.meeting.openItems.columns.text') },
      { key: 'assigneeName', label: t('siteDocs.meeting.openItems.columns.assigneeName'), sortable: true },
      { key: 'assigneeCompany', label: t('siteDocs.meeting.openItems.columns.assigneeCompany') },
      { key: 'dueDate', label: t('siteDocs.meeting.openItems.columns.dueDate'), sortable: true },
      { key: 'since', label: t('siteDocs.meeting.openItems.columns.since'), sortable: true },
      { key: 'lastDiscussed', label: t('siteDocs.meeting.openItems.columns.lastDiscussed'), sortable: true },
      { key: 'documentNumber', label: t('siteDocs.meeting.openItems.columns.documentNumber') },
    ],
    [t, terms.project],
  )

  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() => filterAusAdresse(suchParameter, filterFields))
  // Aus der Projektkarte: `?projectId=…` grenzt auf das Projekt ein — ohne eigenes Filterfeld.
  const projectId = suchParameter.get('projectId') ?? undefined
  const ladeSpalten = (): ColumnState => {
    const g = loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null)
    return g?.columnKeys?.length ? { columnKeys: g.columnKeys, columnWidths: g.columnWidths ?? {}, sortKey: g.sortKey ?? 'dueDate', sortDir: g.sortDir ?? 'asc' } : STANDARD
  }

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(ladeSpalten)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [rows, setRows] = useState<BesprechungOffenerPunkt[]>([])
  const [meta, setMeta] = useState<{ total: number; page: number; totalPages: number } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const params = useCallback((filters: Record<string, ActiveFilter>, state: ColumnState): OffenePunkteParams => ({ ...filterParams(filters), ...(projectId ? { projectId } : {}), sort: state.sortKey, dir: state.sortDir }), [projectId])

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, seite: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        const r = await offenePunkte({ ...params(filters, state), page: seite, limit: 50 })
        setRows(r.data)
        setMeta(r.meta)
      } catch {
        setError(t('siteDocs.list.loadError'))
      } finally {
        setLoading(false)
      }
    },
    [t, params],
  )

  useEffect(() => {
    void fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, 1, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    if (!SORTIERBAR.has(key)) return
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    setPage(1)
    void fetchData(filterValues, 1, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: Math.max(60, startW + ev.clientX - startX) } }))
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))

  const renderCell = (z: BesprechungOffenerPunkt, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'projectLabel':
        return z.projectLabel || '—'
      case 'art':
        return `${z.art} Nr. ${z.laufnummer}`
      case 'text':
        return <span className="whitespace-normal">{z.text}</span>
      case 'assigneeName':
        return z.assigneeName ?? ''
      case 'assigneeCompany':
        return z.assigneeCompany ?? ''
      case 'dueDate':
        return (
          <span className={z.overdue ? 'font-medium text-destructive' : ''} title={z.overdue ? t('siteDocs.meeting.openItems.overdue') : undefined}>
            {[z.dueDate ? fmtDate(z.dueDate) : null, z.dueText].filter(Boolean).join(' · ')}
          </span>
        )
      case 'since':
        return fmtDate(z.since)
      case 'lastDiscussed':
        return fmtDate(z.lastDiscussed)
      case 'documentNumber':
        return z.documentNumber ?? ''
      default:
        return null
    }
  }

  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const pfeil = (key: string) =>
    colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('siteDocs.meeting.openItems.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('siteDocs.meeting.openItems.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            defaultFilename="offene-punkte"
            request={(format, orientation) => offenePunkteExport({ ...params(filterValues, colState), format, orientation, columns: ['number', ...shownColumns.flatMap((c) => (c.key === 'dueDate' ? ['dueDate', 'dueText'] : [c.key]))].join(',') })}
            mail={{ source: 'EXPORT_SITE_MEETING_ITEMS', title: t('mailExport.titles.EXPORT_SITE_MEETING_ITEMS'), subject: t('mailExport.titles.EXPORT_SITE_MEETING_ITEMS') }}
          />
        </div>
      </div>

      <FilterBar availableFields={filterFields} value={filterValues} onChange={handleFiltersChange} layoutKey="site_docs_meeting_open_items" defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS} adresse={adresse} />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title={t('siteDocs.list.columns')}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = (value as ColumnState | null) ?? STANDARD
                markUiSettingApplied(LAYOUT_KEY, next)
                setColState(next)
                void fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="relative px-4 py-1.5 text-left font-medium text-foreground" style={colState.columnWidths.number ? { width: colState.columnWidths.number } : undefined}>
                  <button type="button" onClick={() => handleSort('number')} className="flex items-center gap-1 hover:text-primary">
                    {t('siteDocs.meeting.openItems.columns.number')} {pfeil('number')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, 'number')} />
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 text-left font-medium text-foreground ${columnConfigMode ? 'cursor-grab' : ''} ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => { e.preventDefault(); setDragOverKey(col.key) } : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {col.sortable ? (
                        <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                          {col.label} {pfeil(col.key)}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button type="button" onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))} className="ml-1 text-muted-foreground hover:text-destructive" title={t('siteDocs.list.removeColumn')}>
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">{t('siteDocs.list.addColumn')}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('siteDocs.meeting.openItems.empty')}
                  </td>
                </tr>
              )}
              {rows.map((z, idx) => (
                <tr key={z.id} onClick={() => navigate(`/site-docs/${z.documentId}`)} className={`cursor-pointer border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} transition-colors hover:bg-accent/30`}>
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{z.number}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5">
                      {renderCell(z, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">{t('siteDocs.list.count', { count: meta?.total ?? rows.length })}</td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className="px-4 py-1.5" />
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>{t('siteDocs.list.page', { page: meta.page, pages: meta.totalPages, total: meta.total })}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ‹
              </button>
              <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)} className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40">
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
